import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  DeleteRecordingButton,
  PlayIcon,
  RecordAgainButton,
  RecordButton,
  VIDEO_BUTTON_SIZE,
  RecordSquare,
  RecorderIdle,
  RecorderNotice,
  RecorderTransport,
} from '@/components/media-recorders/RecorderChrome';
import { useRecordingElapsed } from '@/hooks/useRecordingElapsed';
import { useVideoRecorder } from '@/hooks/useVideoRecorder';
import { VIDEO_TIME_LIMIT_SECONDS } from '@/constants';
import { formatTime } from '@/utils/formatters/time';

type VideoRecorderProps = {
  onRecordingStart?: () => void;
  onRecordingComplete: (blob: Blob, url: string, duration: number) => void;
  onRecordingDelete: () => void;
  existingVideoUrl: string;
  existingVideoDuration: number;
  isActive?: boolean;
  /** Slot under the idle labels — the recording ideas link (GIFTWEB-23). */
  idleFooter?: ReactNode;
  /** Playback only — used where the saved message is shown back for review. */
  readOnly?: boolean;
};

const PERMISSION_MESSAGE =
  'We need permission to use your camera and microphone. Allow camera access in your browser, then tap to record.';
const NO_CAMERA_MESSAGE =
  "We couldn't find a camera. Connect one and try again, or leave a voice or written message instead.";
const GENERIC_MESSAGE =
  "We couldn't start recording. Please try again, or leave a voice or written message instead.";

function messageForError(err: unknown): string {
  const name = (err as DOMException)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return PERMISSION_MESSAGE;
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return NO_CAMERA_MESSAGE;
  }
  return GENERIC_MESSAGE;
}

export interface VideoRecorderHandle {
  releaseStream: () => void;
}

const VideoRecorder = forwardRef<VideoRecorderHandle, VideoRecorderProps>(
  function VideoRecorder(
    {
      onRecordingStart,
      onRecordingComplete,
      onRecordingDelete,
      existingVideoUrl,
      existingVideoDuration,
      isActive = true,
      idleFooter,
      readOnly = false,
    },
    ref,
  ) {
    const [videoUrl, setVideoUrl] = useState(existingVideoUrl || '');
    const [duration, setDuration] = useState(existingVideoDuration || 0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    // AC 3: the idle label changes once a take has been made and deleted.
    const [hasRecordedBefore, setHasRecordedBefore] = useState(
      Boolean(existingVideoUrl),
    );

    /**
     * Whether the pre-roll self-view is up (GIFTWEB-39 item 9).
     *
     * The camera is opened as soon as the video tab is selected, so the
     * contributor can frame themselves before recording rather than finding
     * out afterwards. Kept as state, not just a ref, because the idle render
     * switches on it — and false is also the fallback: if the camera cannot be
     * opened for any reason the original idle state stands, unchanged.
     */
    const [isPreviewing, setIsPreviewing] = useState(false);

    const streamRef = useRef<MediaStream | null>(null);
    const previewRef = useRef<HTMLVideoElement | null>(null);
    const playbackRef = useRef<HTMLVideoElement | null>(null);
    const recordingRef = useRef(false);

    // ── Recording engine ──────────────────────────────────────────────────────
    //
    // The custom hook (not react-media-recorder) calls
    // mediaRecorder.start(timeslice) so the browser writes incremental
    // moof/mdat atoms — this is what prevents the ~15s Safari freeze, and it
    // assembles the final blob in ondataavailable so the last chunk is never
    // dropped. Don't collapse it back to a single-blob recorder.
    const { status, previewStream, startRecording, stopRecording, clearBlobUrl } =
      useVideoRecorder({
        timeslice: 1000,
        onStop: (blobUrl, blob) => {
          const capturedDuration = elapsedRef.current;
          setDuration(capturedDuration);
          releaseCamera();
          setVideoUrl(blobUrl);
          setHasRecordedBefore(true);
          onRecordingComplete(blob, blobUrl, capturedDuration);
        },
      });

    const isRecording = status === 'recording';
    recordingRef.current = isRecording;

    /**
     * Whether a pre-roll self-view is wanted right now: the tab is showing,
     * nothing has been recorded yet, and we are neither mid-take nor in the
     * read-only playback mode.
     */
    const wantsPreview = isActive && !readOnly && !videoUrl && !isRecording;
    const previewLiveRef = useRef(wantsPreview);
    previewLiveRef.current = wantsPreview;
    /** Guards against a second, concurrent camera request. */
    const previewPendingRef = useRef(false);

    const releaseCamera = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setIsPreviewing(false);
    };

    const handleStop = () => {
      if (!recordingRef.current) return;
      stopRecording();
    };

    const { elapsed, elapsedRef, reset: resetElapsed } = useRecordingElapsed(
      isRecording,
      VIDEO_TIME_LIMIT_SECONDS,
      handleStop,
    );

    useImperativeHandle(ref, () => ({
      releaseStream: () => {
        handleStop();
        releaseCamera();
      },
    }));

    // Attach the live stream once the recorder hands it over.
    useEffect(() => {
      if (previewStream && previewRef.current) {
        previewRef.current.srcObject = previewStream;
      }
    }, [previewStream]);

    // Open the camera for the pre-roll self-view, and shut it again the moment
    // it stops being wanted — leaving the tab, or starting a take.
    useEffect(() => {
      if (!wantsPreview) {
        if (!recordingRef.current) releaseCamera();
        return;
      }
      void startPreview();
      // startPreview is stable in effect: it only reads refs and setState.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wantsPreview]);

    // Bind the pre-roll stream to its element once that element exists.
    useEffect(() => {
      if (isPreviewing && previewRef.current && streamRef.current) {
        previewRef.current.srcObject = streamRef.current;
      }
    }, [isPreviewing]);

    // Release the camera when the tab is switched away or the page unmounts.
    useEffect(() => {
      return () => {
        if (recordingRef.current) stopRecording();
        releaseCamera();
      };
      // stopRecording's identity changes with the recorder's internals; keying
      // on it would tear the camera down mid-take.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (isActive) return;
      handleStop();
      releaseCamera();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    // Adopt a recording handed in by the parent (refresh recovery).
    useEffect(() => {
      if (!existingVideoUrl || existingVideoUrl === videoUrl) return;
      setVideoUrl(existingVideoUrl);
      setDuration(existingVideoDuration || 0);
      setHasRecordedBefore(true);
      // videoUrl is compared, not depended on: adding it would re-run the sync
      // straight after setVideoUrl and clobber a fresh local recording.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingVideoUrl, existingVideoDuration]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const openCamera = () =>
      navigator.mediaDevices.getUserMedia({ video: true, audio: true });

    /**
     * Open the camera for the pre-roll self-view.
     *
     * Failure is not surfaced: this runs unprompted when the tab is selected,
     * so a denied permission or a browser that wants a user gesture first
     * should simply leave the original idle state in place, not accuse the
     * contributor of anything. Tapping record then asks again, and *that*
     * failure does get a notice.
     */
    const startPreview = async () => {
      // Both checks matter: the first skips a camera we already hold, the
      // second a request still in flight. Without it StrictMode's double
      // effect opens two cameras and throws one away.
      if (streamRef.current || previewPendingRef.current) return;
      previewPendingRef.current = true;
      try {
        const stream = await openCamera();
        // The tab may have been left, or a take started, while we waited — and
        // "Record again" can open its own stream in that window.
        if (!previewLiveRef.current || streamRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        setIsPreviewing(true);
      } catch (err) {
        console.info('VideoRecorder: no pre-roll preview', err);
      } finally {
        previewPendingRef.current = false;
      }
    };

    const handleStart = async () => {
      setNotice(null);
      onRecordingStart?.();

      try {
        // Reuse the pre-roll stream when there is one, so tapping record does
        // not prompt a second time or blink the camera off and on. Only when
        // the preview could not be opened does this ask for the camera itself,
        // which is the original behaviour.
        const stream = streamRef.current ?? (await openCamera());
        streamRef.current = stream;
        setIsPreviewing(false);
        resetElapsed();
        setDuration(0);
        setVideoUrl('');
        setIsPlaying(false);
        startRecording(stream);
      } catch (err) {
        console.error('VideoRecorder: could not start the camera', err);
        releaseCamera();
        setNotice(messageForError(err));
      }
    };

    const handlePlayPause = () => {
      const video = playbackRef.current;
      if (!video) return;
      if (!video.paused) {
        video.pause();
        return;
      }
      // Older browsers return undefined rather than a promise.
      const started = video.play();
      if (started) void started.catch(() => setIsPlaying(false));
    };

    const handleDelete = () => {
      playbackRef.current?.pause();
      setVideoUrl('');
      setIsPlaying(false);
      setDuration(0);
      resetElapsed();
      clearBlobUrl();
      onRecordingDelete();
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const cardClasses = 'mt-4 rounded-2xl bg-[#f3f1ea] p-3.5';
    const stageClasses =
      'relative flex h-[188px] w-full items-center justify-center overflow-hidden rounded-xl bg-[#e6e4de] lg:h-[200px]';
    const transportRowClasses = 'mt-3.5 flex w-full justify-center';

    if (isRecording) {
      return (
        <div className={cardClasses}>
          <div className={stageClasses}>
            {/* Mirrored to match the pre-roll self-view; the recorded file
                itself is not mirrored, so playback below is left alone. */}
            <video
              ref={previewRef}
              autoPlay
              muted
              playsInline
              className='size-full -scale-x-100 object-cover'
            />
            <div className='absolute top-3 left-3 flex items-center gap-[7px] rounded-[9px] bg-[#201f1d]/[0.78] px-2.5 py-[5px]'>
              <span className='size-2 animate-pulse rounded-full bg-[#f43f5e]' />
              <span
                role='timer'
                aria-label='Recording duration'
                data-testid='video-timer'
                className='text-xs font-bold text-white tabular-nums'
              >
                {formatTime(elapsed)} / {formatTime(VIDEO_TIME_LIMIT_SECONDS)}
              </span>
            </div>
          </div>
          <div className={transportRowClasses}>
            <RecorderTransport
              action='stop'
              onClick={handleStop}
              testId='video-stop'
            />
          </div>
        </div>
      );
    }

    if (videoUrl) {
      return (
        <div className={cardClasses}>
          <div className={stageClasses}>
            {/* Contributor-recorded video: there is no caption track to offer. */}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={playbackRef}
              src={videoUrl}
              playsInline
              preload='metadata'
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              className='size-full object-cover'
            />
            {!isPlaying && (
              <button
                type='button'
                onClick={handlePlayPause}
                aria-label='Play recording'
                data-testid='video-play-overlay'
                className='absolute flex size-14 cursor-pointer items-center justify-center rounded-full border-none bg-white/95 p-0 shadow-[0_10px_24px_-12px_rgba(20,40,32,0.6)]'
              >
                <PlayIcon />
              </button>
            )}
            <div className='absolute top-3 left-3 flex items-center gap-[7px] rounded-[9px] bg-[#201f1d]/[0.78] px-2.5 py-[5px]'>
              <span className='size-2 rounded-full bg-[#f43f5e]' />
              <span className='text-xs font-bold text-white tabular-nums'>
                {formatTime(duration)}
              </span>
            </div>
          </div>

          <div className={transportRowClasses}>
            <RecorderTransport
              action={isPlaying ? 'pause' : 'play'}
              onClick={handlePlayPause}
              testId={isPlaying ? 'video-pause' : 'video-play'}
            />
          </div>

          <div className='mt-3 flex items-center justify-between gap-3.5'>
            <div className='flex min-w-0 items-center gap-4'>
              <span
                data-testid='video-duration'
                className='text-[12.5px] text-[#72706f] tabular-nums'
              >
                Duration {formatTime(duration)}
              </span>
              {!readOnly && (
                <RecordAgainButton
                  onClick={() => {
                    handleDelete();
                    void handleStart();
                  }}
                />
              )}
            </div>
            {!readOnly && (
              <DeleteRecordingButton onClick={handleDelete} variant='bare' />
            )}
          </div>
        </div>
      );
    }

    /**
     * Pre-roll: the contributor sees themselves before recording starts
     * (GIFTWEB-39 item 9). Same stage as the take and the playback, so
     * starting to record changes only the controls, not the framing.
     */
    if (isPreviewing) {
      return (
        <>
          <div className={cardClasses}>
            {/* Same labels and footer as the plain idle state, so the prompt
                and the length cap read identically whether or not the camera
                could be opened. */}
            <RecorderIdle
              label={
                hasRecordedBefore ? 'TAP TO RE-RECORD' : 'TAP TO START RECORDING'
              }
              sublabel={`${VIDEO_TIME_LIMIT_SECONDS} SECONDS MAX`}
              footer={idleFooter}
            >
              <div className={stageClasses}>
                <video
                  ref={previewRef}
                  autoPlay
                  muted
                  playsInline
                  data-testid='video-preview'
                  className='size-full -scale-x-100 object-cover'
                />
                {/* RecordButton is `relative` by design, so it is positioned
                    from the outside rather than by adding a prop to the
                    shared chrome. */}
                <span className='absolute'>
                  <RecordButton
                    size={VIDEO_BUTTON_SIZE}
                    onClick={() => void handleStart()}
                    label={hasRecordedBefore ? 'Record again' : 'Start recording'}
                    ring
                    testId='video-record'
                  >
                    <RecordSquare />
                  </RecordButton>
                </span>
              </div>
              <RecorderTransport
                action='start'
                onClick={() => void handleStart()}
                testId='video-start'
                className='mt-3.5'
              />
            </RecorderIdle>
          </div>
          {notice && <RecorderNotice>{notice}</RecorderNotice>}
        </>
      );
    }

    return (
      <>
        <RecorderIdle
          className='py-5'
          label={hasRecordedBefore ? 'TAP TO RE-RECORD' : 'TAP TO START RECORDING'}
          sublabel={`${VIDEO_TIME_LIMIT_SECONDS} SECONDS MAX`}
          footer={idleFooter}
        >
          <RecordButton
            size={VIDEO_BUTTON_SIZE}
            onClick={() => void handleStart()}
            label={hasRecordedBefore ? 'Record again' : 'Start recording'}
            ring
            testId='video-record'
          >
            <RecordSquare />
          </RecordButton>
          <RecorderTransport
            action='start'
            onClick={() => void handleStart()}
            testId='video-start'
            className='mt-3.5'
          />
        </RecorderIdle>
        {notice && <RecorderNotice>{notice}</RecorderNotice>}
      </>
    );
  },
);

export default VideoRecorder;
